import { useMemo, useState } from "react";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { PageHeader, CancelButton, SaveButton } from "@/components/admin/shared/PageHeader";
import { TablePagination } from "@/components/admin/shared/TablePagination";
import { useChangeLog, useChangeLogEntry, useUndoChangeLog } from "@/hooks/useChangeLog";
import type { ChangeLogEntry } from "@/api/changeLog";

/** Pull the structured `{ code, message }` out of an Axios error envelope. */
const apiError = (err: unknown, fallback: string): { code: string | null; message: string } => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: { error?: { code?: string; message?: string } } } })
      .response?.data;
    if (data?.error) {
      return {
        code: data.error.code ?? null,
        message: data.error.message ?? fallback,
      };
    }
  }
  return { code: null, message: err instanceof Error ? err.message : fallback };
};

/**
 * Code returned by svc-04 when the live row drifted since the entry was
 * recorded. The POC had no such guard (undo always applied); we keep the guard
 * but let the admin override it with a one-click "Undo anyway".
 */
const STATE_MISMATCH_CODE = "change-log-state-mismatch";

/**
 * Returned when a LATER bulk price change on the same dataset+region is still
 * standing. Percentage changes compose, so they must be unwound newest-first;
 * forcing writes the older snapshot straight over the newer change.
 */
const NEWER_BULK_PRICE_CODE = "change-log-newer-bulk-price-change";

/** Codes the admin may override with a second, explicit click. */
const FORCEABLE_CODES = new Set([STATE_MISMATCH_CODE, NEWER_BULK_PRICE_CODE]);

/**
 * Friendly singular label for each audit `targetType`. Mirrors the set of
 * undoable targets in svc-04's `RevertDispatcher` registry.
 */
const TARGET_LABELS: Record<string, string> = {
  user: "user",
  role: "role",
  cost_model: "cost model",
  machine_option: "machine option",
  tooling_set: "tooling set",
  fastener_tooling_mapping: "fastener → tooling mapping",
  fastener: "fastener",
  profil_fastener: "Profil fastener",
  product: "product",
  manual_quote_item: "manual quote item",
  responsibility_mapping: "responsibility mapping",
  profile_indie_option: "Profil InDie option",
  sherex_option: "Sherex option",
  autoriv_configurator_item: "AutoRiv configurator item",
  pemserter_price_eur: "PEMSERTER price (EUR)",
  pemserter_price_usd: "PEMSERTER price (USD)",
  calculation_parameter: "calculation parameter",
  cad3d_settings: "3D CAD settings",
  email_settings: "SMTP / email settings",
  landing_category: "landing category",
  landing_meta: "landing meta",
  analytics_event: "analytics event",
  bulk_price_change: "bulk price change",
};

/**
 * svc-04 rejects an undo whose `before` snapshot is missing or truncated. For a
 * bulk price change that means an entry recorded before price snapshots
 * shipped: there is nothing to restore, and re-applying the inverse % would NOT
 * recover the old prices (a −2% then a +2% lands at ×0.9996, not ×1). Detected
 * from the detail payload rather than guessed from the target type.
 */
const BULK_PRICE_NO_SNAPSHOT =
  "This bulk price change was recorded before per-price snapshots shipped, so the original prices aren’t stored and can’t be restored. Re-applying the inverse % would not recover them exactly.";

const targetLabel = (t: string | null | undefined): string =>
  (t ? TARGET_LABELS[t] : undefined) ?? t ?? "record";

/** Undo verb encoded in the action suffix (mirrors svc-04 `ChangeLogService.actionVerb`). */
const undoVerb = (
  actionCode: string | null | undefined,
): "create" | "update" | "delete" | "role" | "other" => {
  if (!actionCode) return "other";
  if (actionCode === "users.role.update") return "role";
  const suffix = actionCode.slice(actionCode.lastIndexOf(".") + 1);
  if (suffix === "create" || suffix === "update" || suffix === "delete") return suffix;
  return "other";
};

/**
 * Plain-language description of what undoing this entry will do to live data —
 * derived from the action verb + target type, so no extra server round-trip is
 * needed. Mirrors the svc-04 revert semantics: create→soft-delete,
 * update→restore previous values, delete→reactivate (never re-insert).
 */
const describeUndoImpact = (entry: ChangeLogEntry): string => {
  const label = targetLabel(entry.targetType);
  const id = entry.targetId ? ` “${entry.targetId}”` : "";
  if (entry.targetType === "bulk_price_change") {
    return `Writes every price back to the exact value it held before this change — the stored snapshot, not the reverse percentage. Prices edited individually since then are overwritten too.`;
  }
  switch (undoVerb(entry.actionCode)) {
    case "create":
      return `Deactivates (soft-deletes) the ${label}${id} this entry created — it stops appearing in admin and customer lists. Nothing is hard-deleted.`;
    case "update":
      return `Restores the ${label}${id} to the values it held before this change. Fields changed by this entry are rewritten to their previous values.`;
    case "delete":
      return `Reactivates the ${label}${id} this entry deleted (clears its soft-delete flag). The original row is restored — not re-created.`;
    case "role":
      return `Restores the user’s previous role assignment (the role held before this change). Cognito group access may need a follow-up role action.`;
    default:
      return `Reverts the recorded ${label}${id} change.`;
  }
};

/** Audit/soft-delete columns that are noise in a business-field diff. */
const DIFF_NOISE_KEYS = new Set([
  "created_at",
  "created_by",
  "updated_at",
  "updated_by",
  "deactivated_at",
  "deactivated_by",
  "createdAt",
  "createdBy",
  "updatedAt",
  "updatedBy",
  "deactivatedAt",
  "deactivatedBy",
]);

/** Compact scalar rendering for a diff cell. Objects/arrays are JSON-stringified and clipped. */
const fmtVal = (v: unknown): string => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v.length > 80 ? `${v.slice(0, 80)}…` : v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  const s = JSON.stringify(v);
  return s.length > 80 ? `${s.slice(0, 80)}…` : s;
};

interface DiffRow {
  key: string;
  before: unknown;
  after: unknown;
}

/**
 * Field-level diff between the recorded before/after snapshots — the concrete
 * values this change touched (undo restores the "before" column). Only keys
 * that actually changed are shown; audit/soft-delete noise is filtered out.
 */
const buildDiff = (
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
): DiffRow[] => {
  const keys = new Set<string>();
  for (const k of Object.keys(before ?? {})) if (!DIFF_NOISE_KEYS.has(k)) keys.add(k);
  for (const k of Object.keys(after ?? {})) if (!DIFF_NOISE_KEYS.has(k)) keys.add(k);
  const rows: DiffRow[] = [];
  for (const key of Array.from(keys).sort()) {
    const b = before?.[key] ?? null;
    const a = after?.[key] ?? null;
    if (JSON.stringify(b) === JSON.stringify(a)) continue;
    rows.push({ key, before: b, after: a });
  }
  return rows;
};

/**
 * The `before_json` svc-04 stores for a bulk price change: the exact prices it
 * overwrote, positional as `[...keyValues, ...priceValues]` per row.
 */
interface BulkPriceSnapshot {
  keyColumns: string[];
  priceColumns: string[];
  rows: (string | number | null)[][];
}

const asBulkPriceSnapshot = (before: unknown): BulkPriceSnapshot | null => {
  if (!before || typeof before !== "object") return null;
  const s = before as Partial<BulkPriceSnapshot>;
  if (!Array.isArray(s.rows) || !Array.isArray(s.keyColumns) || !Array.isArray(s.priceColumns)) {
    return null;
  }
  return { keyColumns: s.keyColumns, priceColumns: s.priceColumns, rows: s.rows };
};

export const ChangeLogPage = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const params = useMemo(
    () => ({ limit: pageSize, offset: pageIndex * pageSize }),
    [pageIndex, pageSize],
  );

  const { data, isLoading, isFetching, isError, error } = useChangeLog(params);

  const undoMutation = useUndoChangeLog();
  const entries: ChangeLogEntry[] = data?.data ?? [];
  const total = data?.total ?? 0;
  const hasNext = pageIndex * pageSize + entries.length < total;

  const [undoTarget, setUndoTarget] = useState<ChangeLogEntry | null>(null);
  const [undoError, setUndoError] = useState<string | null>(null);
  const [undoErrorCode, setUndoErrorCode] = useState<string | null>(null);

  const detailQuery = useChangeLogEntry(undoTarget?.id ?? null);
  const detail = detailQuery.data;
  const isBulkPrice = undoTarget?.targetType === "bulk_price_change";
  const priceSnapshot = useMemo(
    () => (detail && isBulkPrice ? asBulkPriceSnapshot(detail.beforeJson) : null),
    [detail, isBulkPrice],
  );
  // A bulk price entry's raw before/after are a price snapshot and an operation
  // descriptor — a field-level diff of those is noise, so it gets its own view.
  const diffRows = useMemo(
    () => (detail && !isBulkPrice ? buildDiff(detail.beforeJson, detail.afterJson) : []),
    [detail, isBulkPrice],
  );
  const snapshotTruncated = !!detail && (detail.beforeTruncated || detail.afterTruncated);
  // Entries logged before price snapshots shipped carry no restorable `before`.
  const bulkPriceUnrestorable =
    isBulkPrice && !!detail && !detailQuery.isLoading && priceSnapshot === null;

  const resetUndoState = () => {
    setUndoTarget(null);
    setUndoError(null);
    setUndoErrorCode(null);
  };

  const handleUndo = (id: string, force = false) => {
    setUndoError(null);
    setUndoErrorCode(null);
    undoMutation.mutate(
      { id, force },
      {
        onSuccess: () => resetUndoState(),
        onError: (err) => {
          const { code, message } = apiError(err, "Failed to undo this change.");
          setUndoError(message);
          setUndoErrorCode(code);
        },
      },
    );
  };

  const canForce = undoErrorCode !== null && FORCEABLE_CODES.has(undoErrorCode);

  return (
    <>
      <PageHeader title="Change Log" subtitle="Audit trail of admin changes. Entries are read-only; use Undo to revert." />

      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          Failed to load change log{error instanceof Error ? `: ${error.message}` : "."}
        </div>
      )}

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-160 text-body">
            <thead className="bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-800">
              <tr>
                {["Time", "Actor", "Action", "Status", "Undo"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-label text-blue_grey3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {isLoading && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-blue_grey3 text-center text-body">Loading…</td>
                </tr>
              )}
              {!isLoading && entries.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-blue_grey3 text-center text-body">No changes recorded yet.</td>
                </tr>
              )}
              {entries.map((entry) => {
                const isUndone = entry.status === "undone";
                return (
                  <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-3 text-blue_grey2 whitespace-nowrap text-body">{entry.time ?? ""}</td>
                    <td className="px-4 py-3 text-blue_grey whitespace-nowrap text-body">{entry.actor ?? ""}</td>
                    <td className="px-4 py-3 text-blue_grey text-body">{entry.action ?? ""}</td>
                    <td className="px-4 py-3">
                      {isUndone && (
                        <span className="px-2 py-0.5 border border-green-300 text-green-600 rounded whitespace-nowrap text-small">
                          undone
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {isUndone ? (
                        <span className="text-blue_grey3 text-small">Undo</span>
                      ) : (
                        <button
                          onClick={() => { setUndoError(null); setUndoErrorCode(null); setUndoTarget(entry); }}
                          className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-blue_grey dark:text-penn-100 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer whitespace-nowrap text-button"
                        >
                          Undo
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <TablePagination
        pageIndex={pageIndex}
        pageSize={pageSize}
        rowsOnPage={entries.length}
        hasNext={hasNext}
        busy={isFetching}
        onPrev={() => setPageIndex((p) => Math.max(0, p - 1))}
        onNext={() => setPageIndex((p) => p + 1)}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPageIndex(0);
        }}
      />

      <AdminModal
        open={undoTarget !== null}
        onClose={() => { if (!undoMutation.isPending) resetUndoState(); }}
        title="Confirm Undo"
        maxWidth="max-w-sm"
        footer={
          <>
            <CancelButton onClick={resetUndoState} />
            {canForce ? (
              <button
                type="button"
                onClick={() => undoTarget && handleUndo(undoTarget.id, true)}
                disabled={undoMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-all duration-150 cursor-pointer disabled:opacity-60 text-body-medium"
              >
                {undoMutation.isPending ? "Undoing…" : "Undo anyway"}
              </button>
            ) : bulkPriceUnrestorable ? null : (
              <SaveButton
                onClick={() => undoTarget && handleUndo(undoTarget.id)}
                label={undoMutation.isPending ? "Undoing…" : "Confirm Undo"}
              />
            )}
          </>
        }
      >
        <p className="text-blue_grey text-body">
          Undo the change: <strong>{undoTarget?.action ?? ""}</strong>?
        </p>
        {undoTarget && !bulkPriceUnrestorable && (
          <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-body">
            <p className="font-medium text-blue_grey">Impact of this undo</p>
            <p className="mt-1 text-blue_grey2">{describeUndoImpact(undoTarget)}</p>
          </div>
        )}
        {bulkPriceUnrestorable && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800 text-body">
            <p className="font-medium">This change can’t be undone</p>
            <p className="mt-1">{BULK_PRICE_NO_SNAPSHOT}</p>
          </div>
        )}
        {priceSnapshot && (
          <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
            <p className="px-3 py-2 text-blue_grey3 uppercase bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-800 text-small-medium">
              {priceSnapshot.rows.length} price
              {priceSnapshot.rows.length === 1 ? "" : "s"} will be restored to these values
            </p>
            <div className="max-h-48 overflow-y-auto">
              <table className="w-full text-small">
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {priceSnapshot.rows.slice(0, 8).map((row, i) => (
                    <tr key={i}>
                      <td className="px-3 py-1 font-mono text-blue_grey2 break-all">
                        {row.slice(0, priceSnapshot.keyColumns.length).join(" · ")}
                      </td>
                      <td className="px-3 py-1 text-right text-green-700 whitespace-nowrap">
                        {row.slice(priceSnapshot.keyColumns.length).map(fmtVal).join(" / ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {priceSnapshot.rows.length > 8 && (
              <p className="px-3 py-2 text-blue_grey4 border-t border-gray-100 dark:border-gray-800 text-small">
                and {priceSnapshot.rows.length - 8} more
              </p>
            )}
          </div>
        )}
        {undoTarget && !isBulkPrice && (
          <div className="mt-3">
            {detailQuery.isLoading ? (
              <p className="text-blue_grey4 text-small">Loading change details…</p>
            ) : snapshotTruncated ? (
              <p className="text-amber-700 text-small">
                The snapshot for this entry was too large to store in full, so a field-level
                preview isn’t available. Undo may be limited.
              </p>
            ) : diffRows.length > 0 ? (
              <div className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
                <p className="px-3 py-2 text-blue_grey3 uppercase bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-800 text-small-medium">
                  Fields changed by this entry (undo restores “before”)
                </p>
                <table className="w-full text-small">
                  <thead className="text-blue_grey4">
                    <tr>
                      <th className="text-left px-3 py-1 font-medium">Field</th>
                      <th className="text-left px-3 py-1 font-medium">Before</th>
                      <th className="text-left px-3 py-1 font-medium">After</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {diffRows.map((r) => (
                      <tr key={r.key} className="align-top">
                        <td className="px-3 py-1 font-mono text-blue_grey2 whitespace-nowrap">{r.key}</td>
                        <td className="px-3 py-1 text-green-700 break-all">{fmtVal(r.before)}</td>
                        <td className="px-3 py-1 text-blue_grey3 break-all line-through decoration-gray-300">
                          {fmtVal(r.after)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        )}
        {canForce && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800 text-body">
            <p className="font-medium">
              {undoErrorCode === NEWER_BULK_PRICE_CODE
                ? "A later bulk price change on this dataset is still in effect."
                : "This record changed after this entry was logged."}
            </p>
            <p className="mt-1">
              {undoErrorCode === NEWER_BULK_PRICE_CODE
                ? "Percentage changes stack, so they have to be undone newest-first. “Undo anyway” writes these older prices straight over the later change, discarding it."
                : "“Undo anyway” will overwrite those newer changes with the older state described above. Forcing skips the safety check the POC never had — proceed only if you intend to discard the more recent edits."}
            </p>
          </div>
        )}
        {undoError && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-body">
            {undoError}
          </div>
        )}
      </AdminModal>
    </>
  );
};
